// Thin wrapper around Firebase Analytics so call sites stay provider-agnostic.
// On native (Capacitor), events go through the native Firebase SDK via
// @capacitor-firebase/analytics. On web, they use the JS SDK as before.
import { Capacitor } from '@capacitor/core'
import type { User } from 'firebase/auth'
import type { AnalyticsEvents } from './analyticsEvents'
import { logEvent, setUserId, setUserProperties } from 'firebase/analytics'
import { analytics, analyticsReady } from './firebase'
import { log } from './logger'
import { getAppOrigin } from './utils'
import type { AppShell } from './appShell'

export type {
  ScheduleSurface,
  ScheduleFab,
  MapPinAudience,
  PartialRangeEntryPoint,
  SetInterestEntryPoint,
  CheckInEntryPoint,
  SearchEntryPoint,
  CalendarSubscribeMethod,
  CalendarSetting,
  NotificationSetting,
  CheckInGuess,
  AgentAccessKind,
  AnalyticsEvents,
} from './analyticsEvents'

const isNative = Capacitor.isNativePlatform()

let FirebaseAnalytics: typeof import('@capacitor-firebase/analytics').FirebaseAnalytics | null = null
const nativeAnalyticsReady: Promise<void> = isNative
  ? import('@capacitor-firebase/analytics').then(m => {
      FirebaseAnalytics = m.FirebaseAnalytics
    })
  : Promise.resolve()


export type AuthState = 'none' | 'anonymous' | 'signed_in'

// Kept in sync by AuthProvider's onAuthStateChanged. A user property rather
// than a param on every event: it describes the user, and GA4's user-scoped
// dimensions don't count against the 50 event-scoped slots.
let currentAuthState: AuthState | null = null

export function setAnalyticsAuthState(state: AuthState) {
  if (state === currentAuthState) return
  currentAuthState = state
  setAnalyticsUserProperty('auth_state', state)
}

// Registered as user-scoped dimensions in scripts/ga4-sync-definitions.ts
// (USER_DIMENSIONS) — add a key there when adding one here.
export type AnalyticsUserProperty =
  | 'web_version'
  | 'auth_state'
  | 'is_admin'
  | 'region_id'
  | 'audience'
  | 'crew_status'
  | 'app_shell'
  | 'push_enabled'
  | 'ui_locale'

export type AnalyticsAudience = 'team' | 'inner_circle' | 'organic'
export type AnalyticsCrewStatus = 'none' | 'solo' | 'crew'
export type AnalyticsAppShell = AppShell
export type AnalyticsPushEnabled = 'granted' | 'denied' | 'default' | 'unsupported'

// Queued like track(), so an event fired right after a change carries the new value.
export function setAnalyticsUserProperty(key: AnalyticsUserProperty, value: string) {
  whenReady(() => {
    if (isNative) {
      FirebaseAnalytics?.setUserProperty({ key, value })
      return
    }
    if (analytics) setUserProperties(analytics, { [key]: value })
  })
}

// Both SDKs come up asynchronously — isSupported() on web, a dynamic import on
// native — so calls made during the app's first render arrive before either is
// ready. Queue them in order instead of dropping them; without this, every
// mount-time event (the landing page_view, the install banner impression) is
// missing from GA4 while later user-driven events on the same screen report
// normally.
let sdkReady = false
const pendingCalls: Array<() => void> = []
const MAX_PENDING = 100

function whenReady(send: () => void) {
  if (sdkReady) {
    send()
    return
  }
  if (pendingCalls.length >= MAX_PENDING) return
  pendingCalls.push(send)
}

void (isNative ? nativeAnalyticsReady : analyticsReady)
  .catch(() => {})
  .then(() => {
    sdkReady = true
    for (const send of pendingCalls) send()
    pendingCalls.length = 0
  })

export function track<K extends keyof AnalyticsEvents>(name: K, params: AnalyticsEvents[K]) {
  // Booleans don't survive the native path: the Firebase Android/iOS SDKs take
  // only String/Long/Double params, so @capacitor-firebase/analytics drops one
  // and the dimension reads "(not set)". That's why `live` on
  // enter_festival_mode was empty for every iOS/Android user while web reported
  // it fine — the JS SDK stringifies booleans for us. Coercing here sends both
  // paths the same 'true'/'false' web has always sent, so no historical break.
  // Numbers stay numbers: group_count and friends are registered as GA4 custom
  // metrics, which a string would break.
  const enriched: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(params as Record<string, unknown>)) {
    enriched[key] = typeof value === 'boolean' ? String(value) : value
  }
  log('analytics', name, enriched)
  whenReady(() => {
    if (isNative) {
      FirebaseAnalytics?.logEvent({ name, params: enriched })
      return
    }
    if (!analytics) return
    // Cast through `string` to bypass Firebase's reserved-event-name overloads;
    // the AnalyticsEvents catalog (analyticsEvents.ts) is our source of truth for shapes.
    logEvent(analytics, name as string, enriched)
  })
}

export function trackPageView(path: string) {
  track('page_view', {
    page_location: getAppOrigin() + path,
    page_title: document.title,
  })
}

export function authStateLabel(user: User | null): AuthState {
  return !user ? 'none' : user.isAnonymous ? 'anonymous' : 'signed_in'
}

export function setAnalyticsUser(uid: string | null) {
  const webVersion = import.meta.env.VITE_APP_RELEASE as string | undefined
  // Shares the queue with track() so identity is applied in the same order
  // relative to events as it is once the SDK is up.
  whenReady(() => {
    if (isNative) {
      FirebaseAnalytics?.setUserId({ userId: uid ?? '' })
      return
    }
    if (!analytics) return
    setUserId(analytics, uid)
  })
  if (webVersion) setAnalyticsUserProperty('web_version', webVersion)
}
